<template>
  <div
    class="custom-checkbox"
    :class="{ 'custom-checkbox--disabled': disabled, 'custom-checkbox--checked': isChecked }"
    role="checkbox"
    :aria-checked="String(isChecked)"
    :tabindex="disabled ? -1 : 0"
    @click="handleClick"
    @keydown.space.prevent="handleClick"
    @keydown.enter.prevent="handleClick"
  >
    <font-awesome-icon
      v-if="indeterminate"
      :icon="['fas', 'square-minus']"
      :class="iconClass"
      class="custom-checkbox__icon"
    />
    <font-awesome-icon
      v-else-if="isChecked"
      :icon="['fas', 'square-check']"
      :class="iconClass"
      class="custom-checkbox__icon custom-checkbox__icon--checked"
    />
    <font-awesome-icon
      v-else
      :icon="uncheckedIcon"
      :class="iconClass"
      class="custom-checkbox__icon custom-checkbox__icon--unchecked"
    />
  </div>
</template>

<script>
import themeClasses from '@/mixins/themeClasses'

export default {
  name: 'Checkbox',
  mixins: [themeClasses],
  props: {
    inputValue: {
      type: Boolean,
      default: undefined,
    },
    value: {
      type: Boolean,
      default: false,
    },
    indeterminate: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: 'primary',
    },
    uncheckedIcon: {
      type: Array,
      default: () => ['far', 'square'],
    },
  },
  computed: {
    isChecked() {
      // Support both v-model (value) and :input-value patterns
      return this.inputValue !== undefined ? this.inputValue : this.value
    },
    iconClass() {
      if (this.disabled) {
        return 'grey--text text--lighten-1'
      }
      if (this.indeterminate) {
        return 'warning--text'
      }
      if (this.isChecked) {
        return `${this.color}--text`
      }
      return this.themeTextClass('grey--text text--darken-1', 'grey--text text--lighten-2')
    },
  },
  methods: {
    handleClick() {
      if (!this.disabled) {
        this.$emit('change', !this.isChecked)
        this.$emit('input', !this.isChecked)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-checkbox {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  position: relative;

  &--disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid var(--v-primary-base, #1976d2);
    outline-offset: 2px;
    border-radius: 2px;
  }

  &__icon {
    font-size: 20px;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

    &--unchecked {
      &:hover:not(.custom-checkbox--disabled &) {
        transform: scale(1.1);
        opacity: 0.7;
      }
    }

    &--checked {
      animation: checkBounce 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);

      &:hover:not(.custom-checkbox--disabled &) {
        transform: scale(1.05);
        filter: brightness(1.1);
      }
    }
  }

  // More specific selector must come after less specific
  &:not(&--disabled) &--checked &__icon--checked {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
  }

  &:active:not(&--disabled) &__icon {
    transform: scale(0.95);
  }
}

@keyframes checkBounce {
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
